/**
 * Locale-aware formatting utilities using the Intl API.
 *
 * These utilities respect the user's language preference from i18next
 * to format dates, times, numbers, and currency appropriately.
 */

import i18n from 'i18next';

/**
 * Get the current language for formatting.
 * Falls back to 'en' if i18n is not initialized.
 */
function getLocale(): string {
  return i18n.language || 'en';
}

/**
 * Maps our language codes to proper locale codes for Intl API
 */
function getIntlLocale(): string {
  const lang = getLocale();
  // Most of our languages map directly, but pt-PT is already correct
  const localeMap: Record<string, string> = {
    en: 'en-US',
    nl: 'nl-NL',
    de: 'de-DE',
    fr: 'fr-FR',
    es: 'es-ES',
    it: 'it-IT',
    'pt-PT': 'pt-PT',
  };
  return localeMap[lang] || lang;
}

export type DateStyle = 'short' | 'medium' | 'long' | 'full';
export type TimeStyle = 'short' | 'medium' | 'long' | 'full';

/**
 * Format a date using the user's locale.
 *
 * @param date - Date to format (Date object, string, or timestamp)
 * @param style - Date style: 'short' (12/25/24), 'medium' (Dec 25, 2024), 'long' (December 25, 2024), 'full' (Wednesday, December 25, 2024)
 * @returns Formatted date string
 *
 * @example
 * formatDate(new Date()) // "Dec 25, 2024" (en), "25 dec. 2024" (nl)
 * formatDate('2024-12-25', 'short') // "12/25/24" (en), "25-12-24" (nl)
 * formatDate('2024-12-25', 'compact') // "25 Dec" (en), "25 dec." (nl)
 */
export function formatDate(
  date: Date | string | number,
  style: DateStyle | 'compact' = 'medium',
): string {
  const dateObj = date instanceof Date ? date : new Date(date);

  if (isNaN(dateObj.getTime())) {
    return 'Invalid date';
  }

  // 'compact' uses day + short month name (e.g. "17 feb", "25 Dec")
  if (style === 'compact') {
    return new Intl.DateTimeFormat(getIntlLocale(), {
      day: 'numeric',
      month: 'short',
    }).format(dateObj);
  }

  return new Intl.DateTimeFormat(getIntlLocale(), {
    dateStyle: style,
  }).format(dateObj);
}

/**
 * Format a date and time using the user's locale.
 *
 * @param date - Date to format
 * @param dateStyle - Date style (default: 'medium')
 * @param timeStyle - Time style (default: 'short')
 * @returns Formatted datetime string
 *
 * @example
 * formatDateTime(new Date()) // "Dec 25, 2024, 2:30 PM" (en)
 */
export function formatDateTime(
  date: Date | string | number,
  dateStyle: DateStyle = 'medium',
  timeStyle: TimeStyle = 'short',
): string {
  const dateObj = date instanceof Date ? date : new Date(date);

  if (isNaN(dateObj.getTime())) {
    return 'Invalid date';
  }

  return new Intl.DateTimeFormat(getIntlLocale(), {
    dateStyle,
    timeStyle,
  }).format(dateObj);
}

/**
 * Format time only using the user's locale.
 *
 * @param date - Date to extract time from
 * @param style - Time style (default: 'short')
 * @returns Formatted time string
 *
 * @example
 * formatTime(new Date()) // "2:30 PM" (en), "14:30" (nl)
 */
export function formatTime(date: Date | string | number, style: TimeStyle = 'short'): string {
  const dateObj = date instanceof Date ? date : new Date(date);

  if (isNaN(dateObj.getTime())) {
    return 'Invalid time';
  }

  return new Intl.DateTimeFormat(getIntlLocale(), {
    timeStyle: style,
  }).format(dateObj);
}

/**
 * Format a date as relative time (e.g., "2 hours ago", "in 3 days").
 *
 * @param date - Date to format relative to now
 * @param style - Formatting style: 'long' ("in 2 days"), 'short' ("in 2 days"), 'narrow' ("in 2d")
 * @returns Formatted relative time string
 *
 * @example
 * formatRelativeTime(twoDaysAgo) // "2 days ago" (en), "2 dagen geleden" (nl)
 */
export function formatRelativeTime(
  date: Date | string | number,
  style: 'long' | 'short' | 'narrow' = 'long',
): string {
  const dateObj = date instanceof Date ? date : new Date(date);

  if (isNaN(dateObj.getTime())) {
    return 'Invalid date';
  }

  const now = new Date();
  const diffMs = dateObj.getTime() - now.getTime();
  const diffSecs = Math.round(diffMs / 1000);
  const diffMins = Math.round(diffSecs / 60);
  const diffHours = Math.round(diffMins / 60);
  const diffDays = Math.round(diffHours / 24);
  const diffWeeks = Math.round(diffDays / 7);
  const diffMonths = Math.round(diffDays / 30);
  const diffYears = Math.round(diffDays / 365);

  const rtf = new Intl.RelativeTimeFormat(getIntlLocale(), { style });

  // Choose the most appropriate unit
  if (Math.abs(diffSecs) < 60) {
    return rtf.format(diffSecs, 'second');
  } else if (Math.abs(diffMins) < 60) {
    return rtf.format(diffMins, 'minute');
  } else if (Math.abs(diffHours) < 24) {
    return rtf.format(diffHours, 'hour');
  } else if (Math.abs(diffDays) < 7) {
    return rtf.format(diffDays, 'day');
  } else if (Math.abs(diffWeeks) < 4) {
    return rtf.format(diffWeeks, 'week');
  } else if (Math.abs(diffMonths) < 12) {
    return rtf.format(diffMonths, 'month');
  } else {
    return rtf.format(diffYears, 'year');
  }
}

export interface NumberFormatOptions {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  useGrouping?: boolean;
}

/**
 * Format a number using the user's locale.
 *
 * @param value - Number to format
 * @param options - Formatting options
 * @returns Formatted number string
 *
 * @example
 * formatNumber(1234567.89) // "1,234,567.89" (en), "1.234.567,89" (nl)
 */
export function formatNumber(value: number, options: NumberFormatOptions = {}): string {
  if (typeof value !== 'number' || isNaN(value)) {
    return 'Invalid number';
  }

  return new Intl.NumberFormat(getIntlLocale(), options).format(value);
}

/**
 * Format a currency value using the user's locale.
 *
 * @param value - Amount to format
 * @param currency - Currency code (default: 'EUR')
 * @param display - How to display the currency: 'symbol', 'code', 'name'
 * @returns Formatted currency string
 *
 * @example
 * formatCurrency(1234.50) // "€1,234.50" (en), "€ 1.234,50" (nl)
 * formatCurrency(1234.50, 'USD') // "$1,234.50" (en)
 */
export function formatCurrency(
  value: number,
  currency: string = 'EUR',
  display: 'symbol' | 'code' | 'name' = 'symbol',
): string {
  if (typeof value !== 'number' || isNaN(value)) {
    return 'Invalid amount';
  }

  return new Intl.NumberFormat(getIntlLocale(), {
    style: 'currency',
    currency,
    currencyDisplay: display,
  }).format(value);
}

/**
 * Format a percentage value using the user's locale.
 *
 * @param value - Value to format as percentage (0.5 = 50%)
 * @param minimumFractionDigits - Minimum decimal places (default: 0)
 * @param maximumFractionDigits - Maximum decimal places (default: 0)
 * @returns Formatted percentage string
 *
 * @example
 * formatPercent(0.756) // "76%" (en), "76 %" (fr)
 * formatPercent(0.756, 1) // "75.6%"
 */
export function formatPercent(
  value: number,
  minimumFractionDigits: number = 0,
  maximumFractionDigits: number = 0,
): string {
  if (typeof value !== 'number' || isNaN(value)) {
    return 'Invalid value';
  }

  return new Intl.NumberFormat(getIntlLocale(), {
    style: 'percent',
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
}

/**
 * Format a duration in minutes to a human-readable format.
 *
 * @param minutes - Total minutes
 * @param showSeconds - Whether to include seconds (default: false)
 * @returns Formatted duration string
 *
 * @example
 * formatDuration(150) // "2h 30m"
 * formatDuration(45) // "45m"
 * formatDuration(60) // "1h"
 * formatDuration(1440) // "1d"
 */
export function formatDuration(minutes: number, showSeconds: boolean = false): string {
  if (typeof minutes !== 'number' || isNaN(minutes) || minutes < 0) {
    return 'Invalid duration';
  }

  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = Math.floor(minutes % 60);
  const secs = showSeconds ? Math.round((minutes % 1) * 60) : 0;

  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${days}d`);
  }
  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (mins > 0 || (parts.length === 0 && !showSeconds)) {
    parts.push(`${mins}m`);
  }
  if (showSeconds && secs > 0) {
    parts.push(`${secs}s`);
  }

  return parts.join(' ') || '0m';
}

/**
 * Format bytes to human-readable file size.
 *
 * @param bytes - Number of bytes
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted file size string
 *
 * @example
 * formatFileSize(1024) // "1.0 KB"
 * formatFileSize(1234567) // "1.2 MB"
 */
export function formatFileSize(bytes: number, decimals: number = 1): string {
  if (typeof bytes !== 'number' || isNaN(bytes) || bytes < 0) {
    return 'Invalid size';
  }

  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(decimals))} ${sizes[i]}`;
}

/**
 * Format a date range.
 *
 * @param startDate - Start date
 * @param endDate - End date
 * @param style - Date style (default: 'medium')
 * @returns Formatted date range string
 *
 * @example
 * formatDateRange(start, end) // "Dec 25, 2024 – Dec 31, 2024"
 */
export function formatDateRange(
  startDate: Date | string | number,
  endDate: Date | string | number,
  style: DateStyle = 'medium',
): string {
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return 'Invalid date range';
  }

  // Use formatRange if available (not all browsers support it yet)
  try {
    const formatter = new Intl.DateTimeFormat(getIntlLocale(), {
      dateStyle: style,
    });
    if (typeof formatter.formatRange === 'function') {
      return formatter.formatRange(start, end);
    }
  } catch {
    // Fall back to manual formatting
  }

  // Fallback for browsers without formatRange
  return `${formatDate(start, style)} – ${formatDate(end, style)}`;
}
